

import { useState, useEffect } from "react";
import "./Settings.css";
import axios from "axios";

const Settings = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);

  // NOTIFICATION SETTINGS
  const [settings, setSettings] = useState({
    enableAll: false,
    offers: false,
    orders: false,
    whatsapp: false,
  });

  // Toggle main sections
  const toggleDropdown = (section) => {
    setOpenDropdown(openDropdown === section ? null : section);
  };

  // Fetch User Info
  const [userDetails, setUserDetails] = useState({
    userName: "",
    emailId: "",
    mobileNumber: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    image: "",
  });
 
  const [error, setError] = useState("");

  useEffect(() => {
    if (openDropdown !== "generalInfo") return; // only fetch when dropdown opens

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("authToken");

    if (!userId) {
      setError("User not logged in.");
      setLoading(false);
      return;
    }

    const apiUrl = `https://backend.maamaas.com/subscription/api/user/getprofile/${userId}`;

    const fetchProfile = async () => {
      try {
        const response = await fetch(apiUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) throw new Error("Failed to fetch profile");

        const data = await response.json();

        // Save Base64 image into localStorage
        if (data.image) localStorage.setItem("profileImage", data.image);

        setUserDetails({
          userName: data.userName || "",
          emailId: data.emailId || "",
          mobileNumber: data.mobileNumber || "",
          dateOfBirth: data.dateOfBirth || "",
          gender: data.gender || "",
          address: data.address || "",
          image: data.image || localStorage.getItem("profileImage") || "",
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [openDropdown]);

  // Handle Toggle Switches
  const toggleSetting = (key) => {
    if (key === "enableAll") {
      const newValue = !settings.enableAll;
      setSettings({
        enableAll: newValue,
        offers: newValue,
        orders: newValue,
        whatsapp: newValue,
      });
    } else {
      const updated = { ...settings, [key]: !settings[key] };

      const allEnabled =
        updated.offers && updated.orders && updated.whatsapp;

      setSettings({
        ...updated,
        enableAll: allEnabled,
      });
    }
  };

  // Static data until API is used
  const displayData = userData || {
    userName: "Rahul Sharma",
    emailId: "rahul.sharma@example.com",
    mobileNumber: "+91 98765 43210",
    dateOfBirth: "15 March 1990",
    gender: "Male",
    address: "123 Main Street, Mumbai, Maharashtra - 400001",
  };

  return (
    <div className="settings-container">

      {/* NOTIFICATIONS SECTION */}
      <div className="settings-section">
        <button
          onClick={() => toggleDropdown("notifications")}
          className="dropdown-button"
        >
          <div className="button-content">
            <div className="button-info">
              <span className="button-icon">🔔</span>
              <div>
                <h3 className="button-title">Notifications & Preferences</h3>
                <p className="button-description">
                  Manage your notification settings
                </p>
              </div>
            </div>
            <span className="dropdown-arrow">
              {openDropdown === "notifications" ? "▲" : "▼"}
            </span>
          </div>
        </button>

        {openDropdown === "notifications" && (
          <div className="dropdown-content">
            <div className="settings-list">

              {/* Enable All */}
              <div className="setting-item">
                <div className="setting-info">
                  <h4 className="setting-title">Enable All Notifications</h4>
                  <p className="setting-description">
                    Turn on all notification types
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.enableAll}
                    onChange={() => toggleSetting("enableAll")}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {/* Offers */}
              <div className="setting-item">
                <div className="setting-info">
                  <h4 className="setting-title">Offers and Coupons</h4>
                  <p className="setting-description">
                    Receive special offers and discount coupons
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.offers}
                    disabled={settings.enableAll}
                    onChange={() => toggleSetting("offers")}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {/* Orders */}
              <div className="setting-item">
                <div className="setting-info">
                  <h4 className="setting-title">Order Updates</h4>
                  <p className="setting-description">
                    Get notified about your orders and purchases
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.orders}
                    disabled={settings.enableAll}
                    onChange={() => toggleSetting("orders")}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {/* WhatsApp */}
              <div className="setting-item">
                <div className="setting-info">
                  <h4 className="setting-title">WhatsApp Notifications</h4>
                  <p className="setting-description">
                    Receive updates via WhatsApp
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.whatsapp}
                    disabled={settings.enableAll}
                    onChange={() => toggleSetting("whatsapp")}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* USER GENERAL INFO */}
      <div className="settings-section">
        <button
          onClick={() => toggleDropdown("generalInfo")}
          className="dropdown-button"
        >
          <div className="button-content">
            <div className="button-info">
              <span className="button-icon">👤</span>
              <div>
                <h3 className="button-title">General Information</h3>
                <p className="button-description">View your account details</p>
              </div>
            </div>
            <span className="dropdown-arrow">
              {openDropdown === "generalInfo" ? "▲" : "▼"}
            </span>
          </div>
        </button>

        {openDropdown === "generalInfo" && (
        <div className="dropdown-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading your information...</p>
            </div>
          ) : error ? (
            <p className="error-text">{error}</p>
          ) : (
            <div className="user-info-grid">
              <div className="info-item">
                <span className="info-label">Full Name</span>
                <span className="info-value">{userDetails.userName}</span>
              </div>

              <div className="info-item">
                <span className="info-label">Email Address</span>
                <span className="info-value">{userDetails.emailId}</span>
              </div>

              <div className="info-item">
                <span className="info-label">Phone Number</span>
                <span className="info-value">{userDetails.mobileNumber}</span>
              </div>

              {/* <div className="info-item">
                <span className="info-label">Date of Birth</span>
                <span className="info-value">{userDetails.dateOfBirth}</span>
              </div>

              <div className="info-item">
                <span className="info-label">Gender</span>
                <span className="info-value">{userDetails.gender}</span>
              </div>

              <div className="info-item">
                <span className="info-label">Address</span>
                <span className="info-value">{userDetails.address}</span>
              </div> */}
            </div>
          )}
        </div>
      )}
      </div>

    </div>
  );
};

export default Settings;
